import { Leaf, HeartHandshake, Sun } from "lucide-react";

const pillars = [
  {
    icon: Leaf,
    label: "Self-Love",
    text: "Returning home to yourself with tenderness, patience, and acceptance.",
  },
  {
    icon: HeartHandshake,
    label: "Affirmations",
    text: "Reshaping the inner dialogue that quietly shapes your entire life.",
  },
  {
    icon: Sun,
    label: "Mind–Body Healing",
    text: "Releasing limiting beliefs stored in body, breath and belief.",
  },
];

export default function Philosophy() {
  return (
    <section
      id="philosophy"
      data-testid="philosophy-section"
      className="relative py-24 lg:py-32 bg-cream"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-16 items-start">
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-square w-full max-w-md">
            <div className="absolute -inset-4 rounded-[40%_60%_55%_45%/50%_45%_55%_50%] bg-[#D8B4A0]/35" />
            <img
              src="https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=1200&q=80&auto=format&fit=crop"
              alt="Dandelion seeds drifting in golden light — release and rise"
              data-testid="philosophy-image"
              className="relative h-full w-full object-cover rounded-[40%_60%_55%_45%/50%_45%_55%_50%] aw-shadow-lg"
            />
            <div className="absolute -bottom-6 -right-4 bg-forest text-cream p-6 rounded-3xl max-w-[220px] aw-shadow">
              <p className="font-serif-display italic text-2xl leading-tight">
                “Heal Your Life.”
              </p>
              <p className="mt-2 text-xs tracking-eyebrow uppercase text-cream/60">
                Tammy&apos;s promise
              </p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <p className="text-xs tracking-eyebrow uppercase text-sage mb-6">
            The Philosophy
          </p>
          <h2
            data-testid="philosophy-headline"
            className="font-serif-display text-forest text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          >
            A gentle return to your
            <span className="italic"> inner power.</span>
          </h2>
          <p className="mt-8 text-forest-soft text-lg leading-relaxed max-w-xl">
            Align Wellness is a transformation space inspired by self-love,
            affirmations and mind–body healing. Here we support individuals in
            releasing limiting beliefs and reconnecting with the quiet
            knowing that has always lived within them.
          </p>
          <p className="mt-4 text-forest-soft text-lg leading-relaxed max-w-xl">
            Make the unconscious, conscious. Change your thinking — and watch
            your whole life begin to soften, open, and bloom.
          </p>

          <ul
            data-testid="philosophy-tenets"
            className="mt-10 max-w-xl space-y-4 border-l-2 border-[#D8B4A0]/60 pl-6"
          >
            <li className="font-serif-display italic text-forest text-xl sm:text-2xl leading-snug">
              Your thoughts shape your reality.
            </li>
            <li className="font-serif-display italic text-forest text-xl sm:text-2xl leading-snug">
              Healing begins with compassion.
            </li>
            <li className="font-serif-display italic text-forest text-xl sm:text-2xl leading-snug">
              Self-love is the foundation of transformation.
            </li>
          </ul>

          <div className="mt-12 grid sm:grid-cols-3 gap-5">
            {pillars.map(({ icon: Icon, label, text }) => (
              <div
                key={label}
                data-testid={`pillar-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="group rounded-3xl border border-sage/30 bg-white/60 p-6 transition-all duration-500 hover:bg-white hover:-translate-y-1 aw-shadow"
              >
                <div className="h-10 w-10 rounded-full bg-[#EAECE8] flex items-center justify-center text-forest mb-4 group-hover:bg-[#D8B4A0]/60 transition-colors">
                  <Icon size={18} strokeWidth={1.4} />
                </div>
                <p className="font-serif-display text-2xl text-forest leading-tight">
                  {label}
                </p>
                <p className="mt-2 text-sm text-forest-soft leading-relaxed">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
